import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import './SignupForm.css';

function TimezoneForm({ onSignupSuccess, showForm = true }) {
  const [formData, setFormData] = useState({
    email: '',
    timezone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    email: false,
    timezone: false
  });
  const [timezones, setTimezones] = useState([]);
  const [emailFromUrl, setEmailFromUrl] = useState(false);
  const location = useLocation();
  const { id } = useParams();

  // Load data on component mount
  useEffect(() => {
    // Get email from URL parameters
    const params = new URLSearchParams(location.search);
    const email = params.get('email');

    // Generate timezone list
    const timezoneList = generateTimezoneList();
    setTimezones(timezoneList);

    // Always start with no timezone selected
    setFormData(prevState => ({
      ...prevState,
      timezone: ''
    }));

    if (email) {
      // If email is provided in URL, set it and mark as from URL
      setFormData(prevState => ({
        ...prevState,
        email: email
      }));
      setEmailFromUrl(true);
    }
  }, [location]);

  // Generate list of timezones with UTC offsets
  const generateTimezoneList = () => {
    return [
      { name: '(GMT-12:00) International Date Line West', value: 'Etc/GMT+12', offset: '-12' },
      { name: '(GMT-11:00) Midway Island, Samoa', value: 'Pacific/Midway', offset: '-11' },
      { name: '(GMT-10:00) Hawaii', value: 'Pacific/Honolulu', offset: '-10' },
      { name: '(GMT-09:00) Alaska', value: 'America/Anchorage', offset: '-9' },
      { name: '(GMT-08:00) Pacific Time (US & Canada)', value: 'America/Los_Angeles', offset: '-8' },
      { name: '(GMT-07:00) Arizona', value: 'Arizona', offset: '-7' },
      { name: '(GMT-07:00) Mountain Time (US & Canada)', value: 'America/Denver', offset: '-7' },
      { name: '(GMT-06:00) Central Time (US & Canada)', value: 'America/Chicago', offset: '-6' },
      { name: '(GMT-05:00) Eastern Time (US & Canada)', value: 'America/New_York', offset: '-5' },
      { name: '(GMT-04:00) Atlantic Time (Canada)', value: 'America/Halifax', offset: '-4' },
      { name: '(GMT-03:00) Brasilia, Buenos Aires', value: 'America/Sao_Paulo', offset: '-3' },
      { name: '(GMT-02:00) Mid-Atlantic', value: 'Atlantic/South_Georgia', offset: '-2' },
      { name: '(GMT-01:00) Azores', value: 'Atlantic/Azores', offset: '-1' },
      { name: '(GMT+00:00) London, Dublin, Edinburgh', value: 'Europe/London', offset: '0' },
      { name: '(GMT+01:00) Paris, Berlin, Rome, Madrid', value: 'Europe/Paris', offset: '1' },
      { name: '(GMT+02:00) Athens, Istanbul, Helsinki', value: 'Europe/Athens', offset: '2' },
      { name: '(GMT+03:00) Moscow, Baghdad, Kuwait', value: 'Europe/Moscow', offset: '3' },
      { name: '(GMT+04:00) Abu Dhabi, Dubai, Baku', value: 'Asia/Dubai', offset: '4' },
      { name: '(GMT+05:00) Karachi, Tashkent', value: 'Asia/Karachi', offset: '5' },
      { name: '(GMT+05:30) Kolkata, Chennai, Mumbai, New Delhi', value: 'Asia/Kolkata', offset: '5.5' },
      { name: '(GMT+06:00) Dhaka, Almaty', value: 'Asia/Dhaka', offset: '6' },
      { name: '(GMT+07:00) Bangkok, Hanoi, Jakarta', value: 'Asia/Bangkok', offset: '7' },
      { name: '(GMT+08:00) Beijing, Hong Kong, Singapore', value: 'Asia/Shanghai', offset: '8' },
      { name: '(GMT+09:00) Tokyo, Seoul', value: 'Asia/Tokyo', offset: '9' },
      { name: '(GMT+10:00) Sydney, Melbourne, Brisbane', value: 'Australia/Sydney', offset: '10' },
      { name: '(GMT+11:00) Solomon Islands', value: 'Pacific/Guadalcanal', offset: '11' },
      { name: '(GMT+12:00) Auckland, Wellington', value: 'Pacific/Auckland', offset: '12' }
    ];
  };

  // Get UTC offset from selected timezone
  const getUtcOffsetFromTimezone = (selectedTimezone) => {
    const found = timezones.find(tz => tz.value === selectedTimezone);
    return found ? found.value : '0';
  };

  // Handle form field changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });

    // Clear error for the field being edited
    if (fieldErrors[e.target.name]) {
      setFieldErrors({
        ...fieldErrors,
        [e.target.name]: false
      });
      setError('');
    }
  };

  // Simulate API check/update (for development when API is not available)
  const simulateApiCall = (email, timezone) => {
    return new Promise(resolve => {
      setTimeout(() => {
        // For testing, pretend emails with "test" or "exists" are valid accounts
        // All others will show the "not found" error
        if (email.includes('test') || email.includes('exists')) {
          resolve({
            ok: true,
            json: () => Promise.resolve({
              success: true,
              message: 'Timezone updated successfully'
            })
          });
        } else {
          resolve({
            ok: false,
            status: 404,
            json: () => Promise.resolve({
              success: false,
              message: 'This email does not have an account with us',
              field: 'email'
            })
          });
        }
      }, 500);
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Reset all errors
    setError('');
    setFieldErrors({
      email: false,
      timezone: false
    });

    // Validate required fields
    if (!formData.email.trim()) {
      setFieldErrors({ ...fieldErrors, email: true });
      setError('Email address is required');
      setLoading(false);
      return;
    }

    if (!formData.timezone) {
      setFieldErrors({ ...fieldErrors, timezone: true });
      setError('Please select your timezone');
      setLoading(false);
      return;
    }

    // Prepare request data
    const requestData = {
      email: formData.email,
      timezone: formData.timezone
    };

    try {
      // Make the API call
	const response = await fetch('/api/timezone/' + id, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      // Parse the response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error('Failed to parse server response');
      }

      // Check response status
      if (!response.ok || !data.success) {
        // Handle field-specific errors
        if (data.field) {
          setFieldErrors({ ...fieldErrors, [data.field]: true });
        }

        // Set error message
        setError(data.message || 'Failed to update timezone');
        setLoading(false);
        return;
      }

      // Handle success
      const timezoneName = timezones.find(tz => tz.value === formData.timezone)?.name || formData.timezone;

      if (onSignupSuccess) {
        onSignupSuccess(formData.email, timezoneName);
      } else {
        const encodedEmail = encodeURIComponent(formData.email).replace(/%2B/g, '+');
        window.location.href = `/timezone-success?email=${encodedEmail}&timezone=${encodeURIComponent(timezoneName)}`;
      }
    } catch (err) {
      // Handle any unexpected errors
      console.error('Form submission error:', err);

      // For development, you can use simulation as a fallback
      if (process.env.NODE_ENV === 'development') {

        try {
          const simulatedResponse = await simulateApiCall(formData.email, formData.timezone);
          const simulatedData = await simulatedResponse.json();

          if (!simulatedResponse.ok || !simulatedData.success) {
            if (simulatedData.field) {
              setFieldErrors({ ...fieldErrors, [simulatedData.field]: true });
            }
            setError(simulatedData.message || 'Failed to update timezone');
          } else {
            const timezoneName = timezones.find(tz => tz.value === formData.timezone)?.name || formData.timezone;

            if (onSignupSuccess) {
              onSignupSuccess(formData.email, timezoneName);
            } else {
              const encodedEmail = encodeURIComponent(formData.email).replace(/%2B/g, '+');
              window.location.href = `/timezone-success?email=${encodedEmail}&timezone=${encodeURIComponent(timezoneName)}`;
            }
          }
        } catch (simError) {
          setError('Unable to update timezone. Please try again later.');
        }
      } else {
        setError('Unable to update timezone. Please try again later.');
      }

      setLoading(false);
    }
  };

  if (!showForm) return null;

  return (
    <div className="">
      <form onSubmit={handleSubmit} className="" noValidate>
        <div className="pt-1 field">
          <label className="label">Email address
          {fieldErrors.email && (
            <span className="help colorDanger mb-1" dangerouslySetInnerHTML={{ __html: error || 'Email address is required' }} />
          )}
          </label>
          <div className="control">
            <input
              className={`input ${fieldErrors.email ? 'colorDanger error' : ''}`}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={{
                color: fieldErrors.email ? '#ff0000' : 'inherit'
              }}
              readOnly={emailFromUrl}
            />
          </div>
        </div>

        <div className="pt-1 field">
          <label className="label">Your timezone
          {fieldErrors.timezone && (
            <span className="help colorDanger mb-1">{error || 'Timezone is required'}</span>
          )}
          </label>
          <div className="control">
            <div className="select is-fullwidth">
              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                className={fieldErrors.timezone ? 'colorDanger error' : ''}
                style={{
                  color: fieldErrors.timezone ? '#ff0000' : 'inherit'
                }}
                disabled={fieldErrors.email}
                required
              >
                <option value="">Select your timezone</option>
                {timezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="pt-5 field">
          <div className="control">
            <button
              className={`button is-primary is-fullwidth ${loading ? 'is-loading' : ''}`}
              type="submit"
              disabled={loading || fieldErrors.email}
            >
              Update timezone
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default TimezoneForm;
